import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Users, DollarSign, Target, Calendar, Clock, UserCheck,
  CheckSquare, TrendingUp, Activity, FileText, Timer,
  ChevronRight, Loader2, BarChart3
} from "lucide-react";

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, subLabel, colorClass = "bg-[#d4a853]/10 text-[#d4a853]" }) => (
  <Card className="border-slate-200 hover:shadow-md transition-shadow">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-slate-500 font-medium mb-1">{label}</p>
          <p className="text-2xl sm:text-3xl font-bold text-[#1e3a5f]">{value}</p>
          {subLabel && <p className="text-xs text-slate-400 mt-1">{subLabel}</p>}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${colorClass}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Simple Bar Chart Component
const LeadsBarChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-slate-400">
        No data available
      </div>
    );
  }

  const maxLeads = Math.max(...data.map(d => d.leads), 1);

  return (
    <div className="h-48 flex items-end justify-between gap-2 sm:gap-4 px-2">
      {data.map((week, weekIndex) => {
        const height = (week.leads / maxLeads) * 100;
        return (
          <div key={`week-${week.week}`} className="flex-1 flex flex-col items-center gap-2">
            <div className="w-full flex flex-col items-center">
              <span className="text-sm font-semibold text-[#1e3a5f] mb-1">{week.leads}</span>
              <div
                className="w-full max-w-[60px] bg-gradient-to-t from-[#1e3a5f] to-[#2d5a8f] rounded-t-lg transition-all duration-500"
                style={{ height: `${Math.max(height, 8)}%`, minHeight: '16px' }}
              />
            </div>
            <span className="text-[10px] sm:text-xs text-slate-500 text-center leading-tight">
              {week.week_label?.split(' - ')[0] || `Week ${weekIndex + 1}`}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Activity Icon Component
const ActivityIcon = ({ type }) => {
  const iconMap = {
    lead: { Icon: Users, color: "bg-blue-100 text-blue-600" },
    lead_accepted: { Icon: Users, color: "bg-emerald-100 text-emerald-600" },
    lead_viewed: { Icon: Users, color: "bg-slate-100 text-slate-600" },
    estimate: { Icon: FileText, color: "bg-amber-100 text-amber-600" },
    estimate_draft: { Icon: FileText, color: "bg-slate-100 text-slate-600" },
    estimate_sent: { Icon: FileText, color: "bg-blue-100 text-blue-600" },
    estimate_accepted: { Icon: FileText, color: "bg-emerald-100 text-emerald-600" },
    appointment: { Icon: Calendar, color: "bg-purple-100 text-purple-600" },
    appointment_scheduled: { Icon: Calendar, color: "bg-purple-100 text-purple-600" },
    appointment_completed: { Icon: Calendar, color: "bg-emerald-100 text-emerald-600" },
    appointment_cancelled: { Icon: Calendar, color: "bg-red-100 text-red-600" },
    task: { Icon: CheckSquare, color: "bg-teal-100 text-teal-600" },
    task_completed: { Icon: CheckSquare, color: "bg-emerald-100 text-emerald-600" },
    clock: { Icon: Timer, color: "bg-indigo-100 text-indigo-600" },
    time_logged: { Icon: Timer, color: "bg-indigo-100 text-indigo-600" },
  };

  const { Icon, color } = iconMap[type] || iconMap.lead;

  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${color}`}>
      <Icon className="w-4 h-4" />
    </div>
  );
};

// Format relative time
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return "";
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Activity Item Component
const ActivityItem = ({ activity }) => (
  <div className="flex items-start gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors">
    <ActivityIcon type={activity.type} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-[#1e3a5f] truncate">{activity.title}</p>
      <p className="text-xs text-slate-500 truncate">{activity.description}</p>
    </div>
    <span className="text-xs text-slate-400 whitespace-nowrap">
      {formatRelativeTime(activity.timestamp)}
    </span>
  </div>
);

// Empty State
const EmptyActivity = () => (
  <div className="text-center py-8">
    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
      <Activity className="w-6 h-6 text-slate-400" />
    </div>
    <p className="text-slate-500 text-sm">No recent activity</p>
    <p className="text-slate-400 text-xs mt-1">Activity will appear here as you work</p>
  </div>
);

// Main Component
export default function ProReportsPage() {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!token) return;

    try {
      const [summaryRes, activityRes] = await Promise.all([
        axios.get(`${API}/pro/reports/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/pro/reports/activity?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setSummary(summaryRes.data);
      setActivities(activityRes.data.activities || []);
    } catch (error) {
      console.error("Failed to load reports:", error);
      toast.error("Failed to load reports data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4a853]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pro-reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1e3a5f]">Reports</h1>
        <p className="text-slate-500">Your business analytics at a glance</p>
      </div>

      {/* Stats Grid - 2x4 on desktop, 1 column on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
        <StatsCard
          icon={Users}
          label="Total Leads"
          value={summary?.total_leads || 0}
          subLabel="Received all time"
          colorClass="bg-blue-100 text-blue-600"
        />
        <StatsCard
          icon={Target}
          label="Leads Accepted"
          value={summary?.accepted_leads || 0}
          subLabel={`${summary?.acceptance_rate || 0}% acceptance rate`}
          colorClass="bg-emerald-100 text-emerald-600"
        />
        <StatsCard
          icon={DollarSign}
          label="Total Earnings"
          value={`$${(summary?.total_earnings || 0).toLocaleString()}`}
          subLabel="From accepted estimates"
          colorClass="bg-amber-100 text-amber-600"
        />
        <StatsCard
          icon={Calendar}
          label="Appointments"
          value={summary?.appointments_this_week || 0}
          subLabel="This week"
          colorClass="bg-purple-100 text-purple-600"
        />
        <StatsCard
          icon={Clock}
          label="Hours Worked"
          value={`${summary?.hours_worked_this_week || 0}h`}
          subLabel="This week"
          colorClass="bg-indigo-100 text-indigo-600"
        />
        <StatsCard
          icon={UserCheck}
          label="Total Clients"
          value={summary?.total_clients || 0}
          subLabel="Unique customers"
          colorClass="bg-teal-100 text-teal-600"
        />
        <StatsCard
          icon={CheckSquare}
          label="Tasks Completed"
          value={summary?.tasks_completed || 0}
          subLabel="All time"
          colorClass="bg-rose-100 text-rose-600"
        />
        <StatsCard
          icon={TrendingUp}
          label="Acceptance Rate"
          value={`${summary?.acceptance_rate || 0}%`}
          subLabel="Lead conversion"
          colorClass="bg-[#d4a853]/10 text-[#d4a853]"
        />
      </div>

      {/* Charts and Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads Per Week Chart */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#d4a853]" />
              Leads Per Week
            </CardTitle>
            <p className="text-xs text-slate-500">Last 4 weeks</p>
          </CardHeader>
          <CardContent>
            <LeadsBarChart data={summary?.leads_per_week || []} />
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#d4a853]" />
              Recent Activity
            </CardTitle>
            <p className="text-xs text-slate-500">Last 10 actions</p>
          </CardHeader>
          <CardContent className="p-0">
            {activities.length === 0 ? (
              <EmptyActivity />
            ) : (
              <div className="divide-y divide-slate-100 max-h-[360px] overflow-y-auto">
                {activities.map((activity, index) => (
                  <ActivityItem key={activity.id || index} activity={activity} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-slate-200 bg-gradient-to-r from-[#1e3a5f]/5 to-transparent">
        <CardContent className="py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-[#1e3a5f]">Need more insights?</h3>
              <p className="text-sm text-slate-500">Track your time and tasks to see detailed reports</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="border-[#1e3a5f]/20 text-[#1e3a5f]"
                onClick={() => window.location.href = "/pro/timeclock"}
              >
                <Timer className="w-4 h-4 mr-2" />
                Time Clock
              </Button>
              <Button
                variant="outline"
                className="border-[#1e3a5f]/20 text-[#1e3a5f]"
                onClick={() => window.location.href = "/pro/tasks"}
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                Tasks
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
